/** ISO 6346 letter-to-value table for the check-digit sum — multiples of 11 (11, 22, 33) are never assigned. */
const LETTER_VALUES: Record<string, number> = {
  A: 10,
  B: 12,
  C: 13,
  D: 14,
  E: 15,
  F: 16,
  G: 17,
  H: 18,
  I: 19,
  J: 20,
  K: 21,
  L: 23,
  M: 24,
  N: 25,
  O: 26,
  P: 27,
  Q: 28,
  R: 29,
  S: 30,
  T: 31,
  U: 32,
  V: 34,
  W: 35,
  X: 36,
  Y: 37,
  Z: 38,
};

/** 3-letter owner code, a category identifier (U freight, J detachable equipment, Z trailer/chassis), 6-digit serial, 1 check digit. */
const FORMAT = /^[A-Z]{3}[UJZ]\d{7}$/;

export type ContainerNumberCheck = { ok: true } | { ok: false; problem: 'format' | 'check_digit'; reason: string };

function checkDigitOf(first10: string): number {
  const sum = [...first10].reduce((total, char, index) => {
    const value = /[0-9]/.test(char) ? Number(char) : LETTER_VALUES[char];
    return total + value * 2 ** index;
  }, 0);
  const remainder = sum % 11;
  return remainder === 10 ? 0 : remainder;
}

/**
 * Validates a container number's ISO 6346 check digit (PRD → Job intake → Captured per job
 * type (Container)). The two failure kinds get different messages: a malformed number never
 * reaches the check-digit maths, and a well-formed one that simply doesn't check out says so
 * rather than complaining about its shape.
 */
export function checkContainerNumber(number: string): ContainerNumberCheck {
  if (!FORMAT.test(number)) {
    return {
      ok: false,
      problem: 'format',
      reason: 'Expected 4 letters (owner code, then U, J or Z), 6 digits and a check digit — e.g. CSQU3054383',
    };
  }

  const expected = checkDigitOf(number.slice(0, 10));
  const actual = Number(number[10]);
  if (expected !== actual) {
    return { ok: false, problem: 'check_digit', reason: `Expected check digit ${expected}, got ${actual}` };
  }
  return { ok: true };
}
